/**
 * Сервис для работы с чатом
 */
import { HttpContext, httpResource } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { ChatStore } from '@rpgc-ai/store/chat/chat.store';

import type { IChatMessageImage, IChatWithDetails } from '@rpgc-ai/shared-interfaces/interfaces/chat.interface';
import type {
  IMessageImageConfig,
  IPendingVideoGeneration,
  IVideoGenerateConfig,
} from '@rpgc-ai/shared-interfaces/interfaces/message-media.interface';

import { TranslocoService } from '@jsverse/transloco';

import { SessionService } from '../auth/session.service';
import { SKIP_AUTH_ERROR_HANDLING, SKIP_GLOBAL_ERROR_NOTIFICATION } from '../auth/skip-global-error-notification.token';
import { ModalService } from '../modal/modal.service';
import { NotificationsService } from '../notifications/notifications.service';
import { ProfileService } from '../profile/profile.service';
import { StorageService } from '../storage/storage.service';
import { VideoGenerationProgressService } from '../video/video-generation-progress.service';

export interface IVoiceOverConfig {
  voiceId: string;
  messageId: string;
}

export interface IVoiceoverItem {
  id: string;
  title: string;
  description: string | null;
  accent: string; // e.g. "US"
  ageBracket: string; // e.g. "18-25"
  gender: 'MALE' | 'FEMALE' | 'OTHER';
  voiceProvider: string; // e.g. "ELEVENLABS"

  createdAt: string; // ISO date string
  isActive: boolean;
  isDefault: boolean;
  isPremium: boolean;
  visibility: 'PUBLIC' | 'PRIVATE';

  sampleAudioUrl: string;
  usageCount: number;

  defaultForLanguages: string[];
  tags: string[];
}

export interface IImageToDelete {
  messageId: string;
  imageId: string;
}

interface IStoredChatVideoTarget {
  chatId: string;
  messageId: string;
  videoUrl: string;
  createdAt: string;
}

interface IBackendImageError {
  code?: unknown;
  error?: unknown;
  message?: unknown;
  name?: unknown;
  status?: unknown;
  statusCode?: unknown;
  url?: unknown;
}

const STORED_VIDEO_TARGETS_LIMIT = 100;

@Injectable({ providedIn: 'root' })
export class ChatMediaService {
  // Injections
  readonly #profileService = inject(ProfileService);
  readonly #notificationsService = inject(NotificationsService);
  readonly #modalService = inject(ModalService);
  readonly #sessionService = inject(SessionService);
  readonly #storageService = inject(StorageService);
  readonly #translationService = inject(TranslocoService);

  readonly #chatStore = inject(ChatStore);
  readonly #videoProgressService = inject(VideoGenerationProgressService);

  // Variables
  readonly imgGenerateConfig = signal<IMessageImageConfig | null>(null);
  readonly videoGenerateConfig = signal<IVideoGenerateConfig | null>(null);
  readonly pendingVideoGenerations = signal<ReadonlyMap<string, IPendingVideoGeneration>>(new Map());

  /** Есть ли активная генерация (бэк: distributed lock — одна на юзера) */
  readonly isAnyVideoGenerationPending = computed<boolean>(() => this.pendingVideoGenerations().size > 0);
  readonly voiceId = signal<IVoiceOverConfig | null>(null);
  readonly imageToDelete = signal<IImageToDelete | null>(null);

  readonly #imageResource = httpResource<{ id: string; url: string; createdAt: string }>(() => {
    const config = this.imgGenerateConfig();
    if (!config) {
      return;
    }

    const chat = untracked(this.#chatStore.chat);
    if (!chat) {
      return;
    }

    const { messageId, ...body } = config;
    const chatPassword = this.#storageService.getJsonItem<Record<string, string>>('chatEncryptionPasswords', {})[chat.id] ?? '';

    return {
      url: `images/message/${chat.id}/${messageId}`,
      method: 'POST',
      body: { ...body, chatPassword },
      context: new HttpContext().set(SKIP_GLOBAL_ERROR_NOTIFICATION, true),
    };
  });

  readonly #videoResource = httpResource<{ success: boolean; error?: string; result?: Record<string, unknown> }>(() => {
    const config = this.videoGenerateConfig();
    if (!config) {
      return;
    }

    const chat = untracked(this.#chatStore.chat);
    if (!chat) {
      return;
    }
    const chatPassword = this.#storageService.getJsonItem<Record<string, string>>('chatEncryptionPasswords', {})[chat.id] ?? '';

    const { messageId, templateId, workflowId, length } = config;
    return {
      url: `workflow/${workflowId}/trigger`,
      method: 'POST',
      body: {
        chatPassword,
        chatId: chat.id,
        payload: {
          messageId,
          templateId,
          length,
          mode: 'normal',
        },
      },
    };
  });

  readonly deleteImage = httpResource(() => {
    const { imageId } = this.imageToDelete() ?? {};
    if (!imageId) {
      return;
    }

    return {
      url: `images/${imageId}`,
      method: 'DELETE',
    };
  });

  readonly voiceover = httpResource.blob<Blob>(() => {
    const config = this.voiceId();
    const { voiceId, messageId } = config || {};
    const chat = this.#chatStore.chat();

    if (!voiceId || !messageId || !chat) {
      return;
    }
    const chatPassword = this.#storageService.getJsonItem<Record<string, string>>('chatEncryptionPasswords', {})[chat.id] ?? '';

    return {
      url: `elevenlabs/tts?messageId=${messageId}&voiceId=${voiceId}${chatPassword ? `&chatPassword=${chatPassword}` : ''}`,
      method: 'GET',
    };
  });

  readonly voices = httpResource<IVoiceoverItem[]>(() => {
    // TODO TTS is actually only required for the maximum subscription tier
    if (this.#sessionService.isAuthenticated() !== true) {
      return;
    }

    return {
      url: 'elevenlabs/voices',
      method: 'GET',
      context: new HttpContext().set(SKIP_GLOBAL_ERROR_NOTIFICATION, true).set(SKIP_AUTH_ERROR_HANDLING, true),
    };
  });

  constructor() {
    this.#loadMessageImageEffect();
    this.#loadVideoGenerationEffect();
    this.#loadVoiceOverEffect();
    this.#deleteImageFromMessageEffect();
  }

  clearImageGenerationSettings(): void {
    this.imgGenerateConfig.set(null);
  }

  clearVideoGenerationSettings(): void {
    this.videoGenerateConfig.set(null);
    this.pendingVideoGenerations.set(new Map());
    this.#videoProgressService.clearAll();
  }

  addPendingVideoGeneration(messageId: string, chatId: string): void {
    this.pendingVideoGenerations.update((prev) => {
      const next = new Map(prev);
      next.set(messageId, { messageId, chatId });
      return next;
    });
  }

  removePendingVideoGeneration(messageId: string): void {
    this.pendingVideoGenerations.update((prev) => {
      const next = new Map(prev);
      next.delete(messageId);
      return next;
    });
  }

  linkMessageVideoId(chatId: string, messageVideoId: string): void {
    if (!chatId) {
      return;
    }
    this.pendingVideoGenerations.update((prev) => {
      const next = new Map(prev);
      for (const [msgId, entry] of next) {
        if (entry.chatId === chatId && !entry.messageVideoId) {
          next.set(msgId, { ...entry, messageVideoId });
          break;
        }
      }
      return next;
    });
  }

  findMessageIdByVideoId(messageVideoId: string): string | undefined {
    for (const [, entry] of this.pendingVideoGenerations()) {
      if (entry.messageVideoId === messageVideoId) {
        return entry.messageId;
      }
    }
    return undefined;
  }

  rememberCompletedVideoTarget(chatId: string, messageId: string, videoUrl: string): void {
    if (!chatId || !messageId || !videoUrl) {
      return;
    }

    const targets = this.#storageService.getJsonItem<Record<string, IStoredChatVideoTarget>>('chatVideoTargets', {});
    targets[this.#getVideoTargetKey(videoUrl)] = {
      chatId,
      messageId,
      videoUrl,
      createdAt: new Date().toISOString(),
    };

    const limitedTargets = Object.fromEntries(
      Object.entries(targets)
        .sort(([, a], [, b]) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
        .slice(0, STORED_VIDEO_TARGETS_LIMIT),
    );
    this.#storageService.setItem('chatVideoTargets', JSON.stringify(limitedTargets));
  }

  applyStoredVideoTargets(chat: IChatWithDetails): IChatWithDetails {
    const targets = Object.values(this.#storageService.getJsonItem<Record<string, IStoredChatVideoTarget>>('chatVideoTargets', {})).filter(
      (target) => target.chatId === chat.id,
    );

    if (!targets.length || !chat.messages?.length) {
      return chat;
    }

    let messages = chat.messages;
    let changed = false;

    for (const target of targets) {
      const videoKey = this.#getVideoTargetKey(target.videoUrl);
      const sourceIndex = messages.findIndex((message) =>
        (message.videos ?? []).some((video) => this.#getVideoTargetKey(video.videoUrl) === videoKey),
      );
      const targetIndex = messages.findIndex((message) => message.id === target.messageId);

      if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
        continue;
      }

      const sourceMessage = messages[sourceIndex];
      const targetMessage = messages[targetIndex];
      const movedVideo = sourceMessage.videos?.find((video) => this.#getVideoTargetKey(video.videoUrl) === videoKey);

      if (!movedVideo || (targetMessage.videos ?? []).some((video) => this.#getVideoTargetKey(video.videoUrl) === videoKey)) {
        continue;
      }

      messages = messages.map((message, index) => {
        if (index === sourceIndex) {
          return {
            ...message,
            videos: (message.videos ?? []).filter((video) => this.#getVideoTargetKey(video.videoUrl) !== videoKey),
          };
        }

        if (index === targetIndex) {
          return {
            ...message,
            videos: [...(message.videos ?? []), movedVideo],
          };
        }

        return message;
      });
      changed = true;
    }

    return changed ? { ...chat, messages } : chat;
  }

  #getVideoTargetKey(videoUrl: string): string {
    return videoUrl.trim();
  }

  #clearFailedVideoGeneration(): void {
    const config = this.videoGenerateConfig();
    this.videoGenerateConfig.set(null);
    if (config) {
      const pending = this.pendingVideoGenerations().get(config.messageId);
      if (pending?.messageVideoId) {
        this.#videoProgressService.removeProgress(pending.messageVideoId);
      }
      this.removePendingVideoGeneration(config.messageId);
    }
  }

  /**
   * Загружает изображение сообщения
   */
  #loadMessageImageEffect(): void {
    effect(() => {
      const { value, error, isLoading } = this.#imageResource;
      const err = error();
      if (err) {
        this.#notificationsService.addErrorNotification(this.#getImageGenerationErrorMessage(err));
        this.clearImageGenerationSettings();
        return;
      }

      const { id, createdAt, url } = value() ?? {};

      if (isLoading() || !id || !createdAt || !url) {
        return;
      }

      untracked(() => {
        const { messageId } = this.imgGenerateConfig() ?? {};

        if (!messageId) {
          this.clearImageGenerationSettings();
          return;
        }

        const newImage: IChatMessageImage = {
          id,
          createdAt,
          url,
          isDeleted: false,
          messageId,
          status: 'COMPLETED',
        };

        this.clearImageGenerationSettings();

        this.#chatStore.addMessageImageById(messageId, newImage);
        // TODO: Replace after image cost is included in user balance
        // this.#profileService.getUserBalance();
        this.#profileService.forceReloadProfile();
      });
    });
  }

  #loadVideoGenerationEffect(): void {
    effect(() => {
      const { error, value, isLoading } = this.#videoResource;
      // Read all signals unconditionally so Angular tracks them for reactivity
      const response = value();
      const err = error();
      const loading = isLoading();
      const hasInsufficientBalanceError = !!response?.result && response.result['errorCode'] === 'INSUFFICIENT_BALANCE';
      const hasGenericError =
        !hasInsufficientBalanceError && (err || !!response?.error || (!!response?.result && !!response.result['error']));
      if (hasInsufficientBalanceError) {
        this.#modalService.openModal('top-up-balance-video');
        this.#clearFailedVideoGeneration();
        return;
      }
      if (hasGenericError) {
        const rawErrorMessage = response?.result?.['errorMessage'];
        const apiErrorMessage = typeof rawErrorMessage === 'string' ? rawErrorMessage : '';
        const message = apiErrorMessage || this.#translationService.translate('NOTIFICATIONS.CHAT_MESSAGE.VIDEO_GENERATION_ERROR');
        this.#notificationsService.addErrorNotification(message);
        this.#clearFailedVideoGeneration();
        return;
      }
      if (loading) {
        return;
      }
      // Enqueue succeeded — keep pending state until WS delivers the video
      untracked(() => {
        const config = this.videoGenerateConfig();
        if (config) {
          const chat = this.#chatStore.chat();
          if (chat && !this.pendingVideoGenerations().has(config.messageId)) {
            this.addPendingVideoGeneration(config.messageId, chat.id);
          }
          this.videoGenerateConfig.set(null);
          // this.#profileService.forceReloadProfile();
          this.#profileService.getUserBalance();
        }
      });
    });
  }

  #getImageGenerationErrorMessage(error: unknown): string {
    const message = this.#extractBackendErrorMessage(error);

    if (message) {
      return /\bnot\b.*\benough\b.*\btokens\b/i.test(message) ? 'Forbidden: You do not have enough tokens to generate this image' : message;
    }

    const status = this.#extractBackendErrorStatus(error);

    if (status === 400) {
      return 'Bad Request: Generation failed';
    }

    if (status === 403) {
      return 'Forbidden: Feature not available for your current subscription plan or you do not have permission for this chat.';
    }

    if (status === 404) {
      return 'Not Found: Character not found';
    }

    if (status === 422 || this.#extractBackendErrorCode(error) === 'IMAGE_MODEL_UNAVAILABLE') {
      return 'Unprocessable Entity: Image model unavailable';
    }

    if (status === 500) {
      return 'Internal Server Error: Failed to generate image: Failed to generate image prompt';
    }

    return this.#translationService.translate('NOTIFICATIONS.CHAT_MESSAGE.ERROR');
  }

  #extractBackendErrorMessage(error: unknown): string {
    const backendError = this.#asBackendImageError(error);
    const nestedError = this.#asBackendImageError(backendError?.error);
    const message = nestedError?.message ?? (this.#isHttpTransportError(backendError) ? undefined : backendError?.message);

    if (typeof message === 'string') {
      return message;
    }

    if (typeof backendError?.error === 'string') {
      return backendError.error;
    }

    return '';
  }

  #extractBackendErrorStatus(error: unknown): number | null {
    const backendError = this.#asBackendImageError(error);
    const nestedError = this.#asBackendImageError(backendError?.error);
    const status = backendError?.status ?? backendError?.statusCode ?? nestedError?.status ?? nestedError?.statusCode;

    return typeof status === 'number' ? status : null;
  }

  #extractBackendErrorCode(error: unknown): string {
    const backendError = this.#asBackendImageError(error);
    const nestedError = this.#asBackendImageError(backendError?.error);
    const code = nestedError?.code ?? backendError?.code;

    return typeof code === 'string' ? code : '';
  }

  #asBackendImageError(error: unknown): IBackendImageError | null {
    return typeof error === 'object' && error !== null ? (error as IBackendImageError) : null;
  }

  #isHttpTransportError(error: IBackendImageError | null): boolean {
    return error?.name === 'HttpErrorResponse' || (typeof error?.status === 'number' && typeof error.url === 'string');
  }

  #deleteImageFromMessageEffect(): void {
    effect(() => {
      const isLoading = this.deleteImage.isLoading();
      const isError = this.deleteImage.error();
      const hasValue = this.deleteImage.hasValue();

      if (isLoading || !hasValue) {
        return;
      }
      if (isError) {
        this.imageToDelete.set(null);
        return;
      }

      untracked(() => {
        const { messageId, imageId } = this.imageToDelete() ?? {};
        if (!messageId || !imageId) {
          this.imageToDelete.set(null);
          return;
        }

        this.#chatStore.removeMessageImageById(messageId, imageId);

        this.imageToDelete.set(null);
      });
    });
  }

  /**
   * Загружает аудио сообщения
   */
  #loadVoiceOverEffect(): void {
    effect(() => {
      const isLoading = this.voiceover.isLoading();
      const isError = this.voiceover.error();
      if (isError) {
        this.voiceId.set(null);
        return;
      }

      if (isLoading) {
        return;
      }
      untracked(() => {
        const audioBlob = this.voiceover.value();
        const { messageId } = this.voiceId() ?? {};

        if (!audioBlob || !messageId) {
          return;
        }

        const textToSpeechMessage = {
          messageId,
          blob: audioBlob,
        };

        this.#chatStore.setTextToSpeechMessage(textToSpeechMessage);
        this.voiceId.set(null);
      });
    });
  }
}
